/**
 * PER-07 — le fournisseur qui pose l'apparence du théâtre sur toute
 * l'application.
 *
 * Monté tout en haut (`App.tsx`), au-dessus du routeur : les couleurs sont donc
 * en place sur **les quatre espaces** — back-office, contrôle d'accès, portail
 * abonné, et surtout la boutique publique et les deux pages de connexion, qui
 * s'affichent avant toute session. C'est la raison d'être de la route publique
 * `GET /apparence/publique` : une personnalisation qui n'apparaîtrait qu'une
 * fois connecté ne personnaliserait rien de ce qu'un visiteur voit.
 *
 * ## Ce qu'il ne fait pas
 *
 * Il ne bloque jamais le rendu. Tant que la requête n'a pas abouti — ou si elle
 * échoue — l'interface s'affiche avec les couleurs compilées dans
 * `index.css`, c'est-à-dire l'apparence historique du produit. Un écran blanc
 * en attendant les couleurs serait un mauvais échange.
 */

import { useEffect, type ReactNode } from 'react'

import { useApparencePublique } from './api'
import { ContexteApparence } from './contexte'
import { poserVariables } from './variables'

export interface FournisseurApparenceProps {
  readonly children: ReactNode
}

export function FournisseurApparence({ children }: FournisseurApparenceProps) {
  const requete = useApparencePublique()
  const apparence = requete.data ?? null

  useEffect(() => {
    if (apparence === null) return undefined
    /* ★ Le style en ligne sur `<html>` l'emporte sur la règle `:root` de la
       feuille compilée : aucune lutte de spécificité, aucun `!important`. La
       fonction de nettoyage remet les valeurs d'origine — utile au rechargement
       à chaud en développement, et indispensable si un jour l'application
       change de théâtre sans rechargement. */
    return poserVariables(document.documentElement, apparence.echelle, apparence.police)
  }, [apparence])

  useEffect(() => {
    const fichier = apparence?.policeFichier ?? null
    if (fichier === null) return undefined

    /**
     * PER-03 — la police auto-hébergée, chargée depuis NOTRE origine et nulle
     * part ailleurs. La politique de sécurité de contenu de l'API
     * (`font-src 'self' data:`) refuserait de toute façon un domaine tiers :
     * ce préchargement ne peut pas devenir, par accident, un appel sortant.
     *
     * ★ T-28 — la règle `@font-face` elle-même est statique, dans
     * `index.css` (elle ne dépend d'aucun état React : il n'existe qu'une
     * seule police auto-hébergée pour l'instant, Inter). Ce préchargement ne
     * fait qu'accélérer la requête que le navigateur ferait de toute façon dès
     * qu'un texte affiché résout à cette famille — il évite le clignotement
     * (FOUC) le temps que la feuille de style déclenche le téléchargement.
     */
    const lien = document.createElement('link')
    lien.rel = 'preload'
    lien.as = 'font'
    lien.type = 'font/woff2'
    lien.crossOrigin = 'anonymous'
    lien.href = fichier
    document.head.append(lien)
    return () => { lien.remove() }
  }, [apparence?.policeFichier])

  useEffect(() => {
    /* Le titre de l'onglet porte le nom du théâtre : dans une barre d'onglets
       chargée, c'est le seul repère. */
    if (apparence !== null && apparence.nom !== '') document.title = apparence.nom
  }, [apparence])

  return <ContexteApparence value={apparence}>{children}</ContexteApparence>
}
